/* Copyright (c) 2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { v4 } from "uuid";
import { Button, Flex, Text, Spinner, Table, Box } from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import { Fieldset } from "~/components/Forms";
import { EditButton } from "~/components/InlineEdit/EditButton";
import { AddPrerequisites } from "~/components/Prerequisites/AddPrerequisites";
import { InputPrerequisites } from "~/components/Prerequisites/InputPrerequisites";
import { Prerequisite, PrerequisitesItem, Product } from "~/models";
import { ACTIONS, Can } from "~/routing";
import { useClickOutside } from "~/tools";
import {
  ProductLoading,
  ProductSubmitValues,
} from "~/views/productCatalog/types";

type InlinePrerequisitesProductProps = {
  item: Product;
  property: "prerequisites";
  onSubmit: (values: ProductSubmitValues) => Promise<void>;
  list: Array<Prerequisite>;
  setList: React.Dispatch<React.SetStateAction<Prerequisite[]>>;
  isLoading: ProductLoading;
  setIsLoading: React.Dispatch<React.SetStateAction<ProductLoading>>;
  isPrerequisitesEditMode: boolean;
  setIsPrerequisitesEditMode: React.Dispatch<React.SetStateAction<boolean>>;
};

function InlinePrerequisitesProduct({
  item,
  property,
  onSubmit,
  list,
  setList,
  isLoading,
  setIsLoading,
  isPrerequisitesEditMode,
  setIsPrerequisitesEditMode,
}: InlinePrerequisitesProductProps) {
  const { handleSubmit } = useForm();

  const onEdit = () => {
    setIsPrerequisitesEditMode(false);
    setIsLoading((prevState) => ({
      ...prevState,
      [property]: true,
    }));
    const { id, name, pricing, sfProductLink, owner, description } = item;

    const organizeValues: ProductSubmitValues = {
      id,
      name,
      pricing,
      sfProductLink,
      ownerId: owner?.id,
      description,
      [property]: list,
    };
    onSubmit(organizeValues);
  };
  const checkIsEmpty = list?.filter((r: Prerequisite) => r.text.length === 0)
    ?.length;

  const wrapperRef = React.useRef(null);

  useClickOutside(
    wrapperRef,
    () => {
      setIsPrerequisitesEditMode(false);
      setList(item.prerequisites);
    },
    isPrerequisitesEditMode
  );

  const addIdPrerequisites = React.useCallback(() => {
    const mapped = item?.prerequisites?.map((text: PrerequisitesItem) => {
      return { id: v4(), ...text };
    });
    item?.prerequisites && setList(mapped);
  }, [item?.prerequisites, setList]);

  React.useEffect(() => {
    addIdPrerequisites();
  }, [addIdPrerequisites, isPrerequisitesEditMode]);

  const EditForm = (
    <form
      onSubmit={handleSubmit(onEdit)}
      css={{ display: "block", width: "100%" }}
      ref={wrapperRef}
    >
      <Box spacing="spacing04">
        <Fieldset>
          <Flex alignItems="center" justifyContent="center">
            <Text weight="medium">Prerequisites</Text>
            <AddPrerequisites list={list} setList={setList} />
          </Flex>
          <Table>
            <Table.tbody>
              {list?.length !== 0 ? (
                list?.map((r: Prerequisite) => {
                  return (
                    <InputPrerequisites
                      key={r.id}
                      item={r}
                      setList={setList}
                      list={list}
                    />
                  );
                })
              ) : (
                <Table.tr>
                  <Table.td>
                    <Text tag="span">No data.</Text>
                  </Table.td>
                </Table.tr>
              )}
            </Table.tbody>
          </Table>
        </Fieldset>
        <Flex>
          <Button
            ml=".5rem"
            type="submit"
            icon="save"
            size="md"
            severity="low"
            disabled={checkIsEmpty !== 0}
          >
            Save
          </Button>
          <Button
            ml=".5rem"
            icon="close"
            size="md"
            severity="low"
            onClick={() => {
              setIsPrerequisitesEditMode(false);
            }}
          >
            Cancel
          </Button>
        </Flex>
      </Box>
    </form>
  );
  const DisplayText = (
    <Box spacing="spacing04">
      <Flex>
        <Text css={{ marginTop: "0.75rem" }} weight="medium">
          Prerequisites
        </Text>
        <Can action={ACTIONS.EDIT_PREREQUISITES}>
          <EditButton onClick={() => setIsPrerequisitesEditMode(true)} />
        </Can>
      </Flex>
      <Table striped>
        <Table.tbody>
          {item[property]?.length !== 0 ? (
            item[property]?.map((p: Prerequisite) => {
              return (
                <Table.tr key={`${p.text}${p.id}`}>
                  <Table.td>{p?.text}</Table.td>
                </Table.tr>
              );
            })
          ) : (
            <Table.tr>
              <Table.td>
                <Text tag="span">No data.</Text>
              </Table.td>
            </Table.tr>
          )}
        </Table.tbody>
      </Table>
    </Box>
  );

  if (isLoading[property]) {
    return (
      <Flex>
        <Spinner size="sm" />
        <Text weight="regular" tag="div" css={{ marginLeft: "1rem" }}>
          Loading ...
        </Text>
      </Flex>
    );
  }

  if (isPrerequisitesEditMode) {
    return EditForm;
  }

  return <FlexContainer align="center">{DisplayText}</FlexContainer>;
}

export { InlinePrerequisitesProduct };
